import { Swiper } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/free-mode";
import "swiper/css/grid";
import {
  Autoplay,
  Navigation,
  FreeMode,
  Pagination,
  Grid,
} from "swiper/modules";
import { useEffect, useRef } from "react";

interface SliderProps {
  children: React.ReactNode;
  options?: any;
  className?: string;
  swiperRef?: any;
}

export const Slider = ({
  children,
  options,
  className = "w-full",
  swiperRef,
}: SliderProps) => {
  const defaultSwiperOptions = {
    spaceBetween: 10,
    centeredSlides: false,
    modules: [Navigation, Autoplay, FreeMode],
    freeMode: true,
    cssMode: true,
  };

  const swiperOptions = {
    ...defaultSwiperOptions,
    ...options,
  };

  return (
    <Swiper {...swiperOptions} className={`${className}`} ref={swiperRef}>
      {children}
    </Swiper>
  );
};
export const GallerySlider = ({ children, options, className = "" }: any) => {
  const swiperOptions = {
    spaceBetween: 30,
    centeredSlides: true,
    loop: true,
    pagination: {
      clickable: true,
      dynamicBullets: true,
    },
    navigation: false,
    modules: [Autoplay, Pagination, Navigation],
    ...options,
  };

  return (
    <Swiper {...swiperOptions} className={`w-full ${className}`}>
      {children}
    </Swiper>
  );
};

export const SliderComponent = ({ children, options }: any) => {
  const swiperOptions = {
    spaceBetween: 5,
    centeredSlides: false,
    modules: [Navigation, Autoplay, FreeMode],
    freeMode: true,
    cssMode: true,
    slidesPerView: 6,
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev",
    },
    breakpoints: {
      320: {
        slidesPerView: 2,
        spaceBetween: 10,
      },
      640: {
        slidesPerView: 3,
        spaceBetween: 10,
      },
      1080: {
        slidesPerView: 5,
        spaceBetween: 10,
      },
      1440: {
        slidesPerView: 6,
        spaceBetween: 2,
      },
    },
    ...options,
  };

  return (
    <Swiper {...swiperOptions} className={`w-full`}>
      {children}
    </Swiper>
  );
};

export const ComponentSlider = ({
  children,
  options,
  setIsBeginning,
  setIsEnd,
}: any) => {
  const swiperRef: any = useRef(null);
  const swiperOptions = {
    spaceBetween: 10,
    centeredSlides: false,
    modules: [Navigation, Autoplay, FreeMode],
    freeMode: true,
    cssMode: true,
    slidesPerView: 3,
    navigation: {
      nextEl: "#custom-next-button",
      prevEl: "#custom-prev-button",
    },
    watchOverflow: true,
    onInit: (swiper: any) => {
      swiperRef.current = swiper;
      swiper.update();
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    },
    onSlideChange: (swiper: any) => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    },
    onReachEnd: () => {
      setIsEnd(true);
    },
    onReachBeginning: () => {
      setIsBeginning(true);
    },
    breakpoints: {
      320: {
        slidesPerView: 1,
        spaceBetween: 5,
      },
      540: {
        slidesPerView: 2,
        spaceBetween: 5,
      },
      768: {
        slidesPerView: 3,
        spaceBetween: 5,
      },
      1024: {
        slidesPerView: 4,
        spaceBetween: 20,
      },
      1440: {
        slidesPerView: 5,
        spaceBetween: 20,
      },
    },
    ...options,
  };

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.navigation.init();
      swiperRef.current.navigation.update();
      setIsBeginning(swiperRef.current.isBeginning);
      setIsEnd(swiperRef.current.isEnd);
    }
  }, [children]);

  return (
    <Swiper {...swiperOptions} className={`w-full`}>
      {children}
    </Swiper>
  );
};

export const BgImageSlider = ({ children, onSwiper }: any) => {
  const swiperOptions = {
    spaceBetween: 0,
    slidesPerView: 1,
    modules: [Autoplay],
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
    },
    loop: true,
    onSwiper,
  };

  return (
    <Swiper {...swiperOptions} className="w-full h-full">
      {children}
    </Swiper>
  );
};

export const GroupSlider = ({
  children,
  options,
  isNavigation = false,
}: any) => {
  const swiperOptions = {
    centeredSlides: false,
    modules: [Navigation, Pagination, Grid],
    ...options,
  };

  return (
    <Swiper {...swiperOptions} className={`w-full `}>
      {children}
      {isNavigation && <div className="mt-7"></div>}
    </Swiper>
  );
};
